<template>
  <v-card>
    <v-card-title class="headline grey lighten-2">
      {{ $t("chapter.chapterLocation") }}
    </v-card-title>
    <v-btn
      absolute
      class="rounded-lg pa-1"
      min-width="0"
      small
      right
      elevation="0"
      top
      @click="$emit('dialog', false)"
      color="accentElement"
      ><v-icon color="white">mdi-close</v-icon></v-btn
    >
    <div class="google-map" ref="googleMap"></div>
    <v-divider></v-divider>
    <v-container>
      <v-row>
        <v-col>
          <CoordinatesTextField
            :latitude="position.latitude"
            :longitude="position.longitude"
          />
        </v-col>
        <v-col>
          <v-text-field  :label="$t('chapter.streetAddress')" disabled dense :value="locationName" outlined></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-slider
            :label="$t('chapter.radiusValue', { radius })"
            v-model="radius"
            :min="minRadius"
            :max="maxRadius"
            step="1"
            thumb-label
            color="primaryAction"
            track-color="primaryAction"
          ></v-slider>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>
<script>
import { Loader } from "@googlemaps/js-api-loader";
import { chapterQueries } from "@/graphql/ChapterQueries";
import { DEFAULT_MAP_ZOOM } from "@/const";
import CoordinatesTextField from "./CoordinatesTextField";
import debounce from "@/helpers/debounce";
const DEFAULT_RADIUS_METERS = 30;
const MIN_RADIUS_METERS = 15;
const MAX_RADIUS_METERS = 100;
const DEFAULT_LAT_EDMONTON = 53.5461;
const DEFAULT_LNG_EDMONTON = 113.4938;
export default {
  components: {
    CoordinatesTextField,
  },
  props: {
    chapter: Object,
    markerSettings: Object,
  },
  data() {
    return {
      loader: null,
      map: null,
      locationName: null,
      geocoder: null,
      marker: null,
      radius: DEFAULT_RADIUS_METERS,
      minRadius: MIN_RADIUS_METERS,
      maxRadius: MAX_RADIUS_METERS,
    };
  },
  computed: {
    position() {
      if (this.marker) {
        const center = this.marker.getCenter();
        return { latitude: center.lat(), longitude: center.lng() };
      }
      return { latitude: undefined, longitude: undefined };
    },
  },
  mounted() {
    if (this.chapter.place == null) {
      this.moveMapToUsersLocation();

      this.clearMarker();
    } else {
      if (this.chapter.place.name) {
        this.locationName = this.chapter.place.name;
      }
    }

    this.loader = new Loader({
      apiKey: process.env.VUE_APP_GOOGLE_API,
      version: "weekly",
      libraries: ["drawing"],
    });
    this.initializeMap();
  },
  methods: {
    clearMarker() {
      if (this.marker) {
        this.marker.setMap(null);
        this.marker = null;
      }
    },
    moveMapToUsersLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          if (this.map != null) {
            this.map.panTo({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          }
        });
      }
    },
    initializeMap() {
      this.loader.load().then(this.handleLoad);
    },
    debouncedSave: debounce(function () {
      this.savePlace();
    }, 500),
    updateMarker(pos) {
      this.clearMarker();

      // eslint-disable-next-line
      this.marker = new google.maps.Circle({
        ...this.markerSettings,
        center: pos,
        radius: this.radius,
        map: this.map,
        draggable: true,
        editable: true,
      });
      this.marker.addListener("center_changed", this.handleMarkerMoved);
      this.marker.addListener("radius_changed", this.handleRadiusChanged);
    },
    handleClick(event) {
      this.updateMarker({ lat: event.latLng.lat(), lng: event.latLng.lng() });
      this.savePlace();
    },
    handleMarkerMoved() {
      this.debouncedSave();
    },
    handleRadiusChanged() {
      let radius = this.marker.getRadius();
      if (radius > MAX_RADIUS_METERS) {
        radius = MAX_RADIUS_METERS;
        this.marker.setRadius(radius);
      } else if (radius < MIN_RADIUS_METERS) {
        radius = MIN_RADIUS_METERS;
        this.marker.setRadius(radius);
      }
      this.radius = radius;
    },
    handleLoad() {
      try {
        const mapContainer = this.$refs.googleMap;

        let mapOptions = {
          zoom: DEFAULT_MAP_ZOOM,
        };
        if (
          this.position.latitude != undefined ||
          this.position.longitude != undefined
        ) {
          mapOptions.center = {
            lat: this.position.latitude,
            lng: this.position.longitude,
          };
        } else {
          // TODO - Should go to user's profile's city
          mapOptions.center = {
            lat: DEFAULT_LAT_EDMONTON,
            lng: DEFAULT_LNG_EDMONTON,
          };
        }

        // eslint-disable-next-line
        let map = new google.maps.Map(mapContainer, mapOptions);
        this.map = map;

        // eslint-disable-next-line
        const geocoder = new google.maps.Geocoder();
        this.geocoder = geocoder;

        if (
          this.position.latitude == undefined ||
          this.position.longitude == undefined
        ) {
          this.moveMapToUsersLocation();
        }

        // drawingManager.setMap(this.map)
        this.map.addListener("click", this.handleClick);
        if (this.chapter.place) {
          this.updateMarker({
            lat: this.chapter.place.latitude,
            lng: this.chapter.place.longitude,
          });
        }
      } catch (error) {
        console.log(error);
      }
    },
    async savePlace() {
      let variables = {
        chapterId: this.chapter.id,
        latitude: this.position.latitude,
        longitude: this.position.longitude,
        radius: this.radius,
      };
      const latlng = {
        lat: this.position.latitude,
        lng: this.position.longitude,
      };
      try {
        const response = await this.geocoder.geocode({ location: latlng });
        if (response) {
          const firstResult = response.results?.[0];
          if (firstResult) {
            variables.name =
              firstResult.address_components?.[0].short_name +
              " " +
              firstResult.address_components?.[1].short_name;
            this.locationName = variables.name;
          }
        }
      } catch (e) {
        console.log(e);
      }
      let refetchQueries = [];
      if (this.chapter.place == null) {
        refetchQueries.push("GetStoryWithChapters");
      }
      this.$apollo.mutate({
        mutation: chapterQueries.updateChapterPlace,
        variables: variables,
        refetchQueries: refetchQueries,
      });
    },
  },
  watch: {
    radius(newValue) {
      if (this.marker != null) {
        this.marker.setRadius(newValue);
        this.debouncedSave();
      }
    },
    chapter() {
      if (this.chapter.place != null) {
        let latLng = {
          lat: this.chapter.place.latitude,
          lng: this.chapter.place.longitude,
        };

        if (this.chapter.place.name) {
          this.locationName = this.chapter.place.name;
        }

        this.updateMarker(latLng);
        this.map.panTo(latLng);
        this.map.setZoom(DEFAULT_MAP_ZOOM);
      } else {
        this.clearMarker();
        this.moveMapToUsersLocation();
      }
    },
  },
};
</script>

<style scoped>
.google-map {
  width: 100%;
  height: 400px;
}
</style>
